import type { ScopedProject } from "@domain/project/project-guards";
import Excel from "exceljs";
import {
  ProductListLayoutBlock,
  ProductListSheetFormat,
  getProductListAddressLayout,
  getProductListChecklistLayout,
  getProductListHeaderLayout,
  getProductListSheetFormat,
  getProductListTotalsLayout,
} from "./export-product-list.layout";
import { ProductListTheme } from "./export-product-list.theme";

export class ExportProductListRenderer {
  constructor(
    private theme: ProductListTheme,
    private lineBreak: string,
  ) {}

  writeHeader(worksheet: Excel.Worksheet) {
    this.applyLayoutBlock(worksheet, getProductListHeaderLayout(this.theme));
  }

  writeAddress(worksheet: Excel.Worksheet, project: ScopedProject) {
    this.applyLayoutBlock(worksheet, getProductListAddressLayout(project, this.theme, this.lineBreak));
  }

  writeTotals(
    worksheet: Excel.Worksheet,
    row: number,
    firstRowOfServices: number,
    lastRowOfServices: number,
    areCurrenciesMixed = false,
  ): number {
    const startRow = row + 1;
    this.applyLayoutBlock(
      worksheet,
      getProductListTotalsLayout(startRow, firstRowOfServices, lastRowOfServices, this.theme, areCurrenciesMixed),
    );
    return startRow + 5;
  }

  writeChecklist(worksheet: Excel.Worksheet, row: number) {
    const startRow = row + 6;
    this.applyLayoutBlock(worksheet, getProductListChecklistLayout(startRow, this.theme));
  }

  writeSheetFormat(worksheet: Excel.Worksheet, currencyFormat: string, percentFormat: string) {
    const format = getProductListSheetFormat(currencyFormat, percentFormat);
    this.applySheetFormat(worksheet, format);
  }

  private applySheetFormat(worksheet: Excel.Worksheet, format: ProductListSheetFormat) {
    format.rowHeights?.forEach((rowDef) => {
      worksheet.getRow(rowDef.row).height = rowDef.height;
    });

    format.columns.forEach((columnDef) => {
      const column = worksheet.getColumn(columnDef.column);
      if (columnDef.width !== undefined) {
        column.width = columnDef.width;
      }
      if (columnDef.hidden !== undefined) {
        column.hidden = columnDef.hidden;
      }
      if (columnDef.alignment) {
        column.alignment = columnDef.alignment;
      }
      if (columnDef.numFmt) {
        column.numFmt = columnDef.numFmt;
      }
    });
  }

  private applyLayoutBlock(worksheet: Excel.Worksheet, block: ProductListLayoutBlock) {
    block.merges?.forEach((cells) => worksheet.mergeCells(cells));
    block.rows?.forEach((rowDef) => {
      const row = worksheet.getRow(rowDef.row);

      if (rowDef.height !== undefined) {
        row.height = rowDef.height;
      }
      if (rowDef.font) {
        row.font = rowDef.font;
      }
    });
    block.cells.forEach((cellDef) => {
      const cell = worksheet.getCell(cellDef.address);

      if (cellDef.value !== undefined) {
        cell.value = cellDef.value;
      }
      if (cellDef.fill) {
        cell.fill = cellDef.fill;
      }
      if (cellDef.border) {
        cell.border = cellDef.border;
      }
      if (cellDef.alignment) {
        cell.alignment = cellDef.alignment;
      }
      if (cellDef.font) {
        cell.font = cellDef.font;
      }
      if (cellDef.numFmt) {
        cell.numFmt = cellDef.numFmt;
      }
      if (cellDef.validationOptions) {
        this.setListValidation(cell, cellDef.validationOptions);
      }
    });
  }

  private setListValidation(cell: Excel.Cell, options: string) {
    const escaped = options.replace(/"/g, '""');
    cell.dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: ['"' + escaped + '"'],
    };
  }
}
